import { useMutation } from '@tanstack/react-query';
import { createAuthenticatedClient } from '@/lib/graphql';
import {
  GenerateUploadUrlDocument,
  type GenerateUploadUrlMutation,
  type GenerateUploadUrlInput,
  type AssetType,
} from '@/graphql/generated/graphql';

export type { AssetType };

interface UploadResult {
  s3Key: string;
}

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

async function generateUploadUrl(
  input: GenerateUploadUrlInput,
  accessToken: string
) {
  const client = createAuthenticatedClient(accessToken);
  const response = await client.request<GenerateUploadUrlMutation>(
    GenerateUploadUrlDocument,
    { input }
  );
  return response.generateUploadUrl;
}

async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

export function useGenerateUploadUrl(accessToken: string) {
  return useMutation({
    mutationFn: (input: GenerateUploadUrlInput) =>
      generateUploadUrl(input, accessToken),
  });
}

interface UseUploadFileOptions {
  topicSlug: string;
  assetType: AssetType;
  onProgress?: (progress: UploadProgress) => void;
}

export function useUploadFile(accessToken: string) {
  const generateUrl = useGenerateUploadUrl(accessToken);

  return useMutation({
    mutationFn: async ({
      file,
      options,
    }: {
      file: File;
      options: UseUploadFileOptions;
    }): Promise<UploadResult> => {
      // Step 1: Get presigned URL from backend
      const result = await generateUrl.mutateAsync({
        topicSlug: options.topicSlug,
        fileName: file.name,
        contentType: file.type,
        assetType: options.assetType,
      });

      const { uploadUrl, s3Key } = result;

      // Check for errors from mutation
      if (!uploadUrl || !s3Key) {
        throw new Error('Failed to generate upload URL');
      }

      // Step 2: Upload file directly to S3
      await uploadFileToS3(file, uploadUrl, options.onProgress);

      return { s3Key };
    },
  });
}

// Utility to get content type from file
export function getContentTypeFromFile(file: File): string {
  return file.type || 'application/octet-stream';
}

// File validation helpers
const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function validateAudioFile(file: File): string | null {
  if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
    return `Invalid file type. Allowed types: ${ALLOWED_AUDIO_TYPES.join(', ')}`;
  }
  if (file.size > MAX_AUDIO_SIZE) {
    return `File too large. Maximum size is ${MAX_AUDIO_SIZE / 1024 / 1024}MB`;
  }
  return null;
}

export function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return `Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`;
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return `File too large. Maximum size is ${MAX_IMAGE_SIZE / 1024 / 1024}MB`;
  }
  return null;
}
